export type DistanceWindow = {
  start: number;
  end: number;
  leader: number;
};

export function clampElapsedSeconds(
  elapsedSeconds: number,
  raceDurationMs: number,
): number {
  const durationSeconds = Math.max(0, raceDurationMs / 1_000);
  return Math.max(0, Math.min(durationSeconds, elapsedSeconds));
}

export function liveElapsedSeconds({
  nowMs,
  serverOffsetMs,
  gatesOpenAt,
}: {
  nowMs: number;
  serverOffsetMs: number;
  gatesOpenAt: string;
}): number {
  // `raceDurationMs` is the nominal course duration, not the end of the
  // visual timeline. Deterministic finish times can extend beyond it and
  // each horse then needs four more seconds to decelerate into its animated
  // idle. The donor's multiplayer clock keeps advancing through that whole
  // intermission; only archive replay controls clamp to the envelope.
  return Math.max(
    0,
    (nowMs + serverOffsetMs - Date.parse(gatesOpenAt)) / 1_000,
  );
}

export function replayElapsedSeconds({
  accumulatedMs,
  startedAtMs,
  nowMs,
  playing,
  raceDurationMs,
}: {
  accumulatedMs: number;
  startedAtMs: number;
  nowMs: number;
  playing: boolean;
  raceDurationMs: number;
}): number {
  const elapsedMs =
    accumulatedMs + (playing ? Math.max(0, nowMs - startedAtMs) : 0);
  return clampElapsedSeconds(elapsedMs / 1_000, raceDurationMs);
}

export function resolveDistanceWindow(
  positions: number[],
  raceDistanceM: number,
  compact = false,
): DistanceWindow {
  const distance = Math.max(1, raceDistanceM);
  const normalized = positions.map((position) =>
    Math.max(0, Math.min(distance, position)),
  );
  const leader = normalized.length ? Math.max(...normalized) : 0;
  const trailer = normalized.length ? Math.min(...normalized) : 0;
  const minimumWindow = compact ? 220 : 280;
  const preferredWindow = distance * (compact ? 0.42 : 0.34);
  const packWindow = leader - trailer + (compact ? 72 : 110);
  const windowSize = Math.min(
    distance,
    Math.max(minimumWindow, preferredWindow, packWindow),
  );
  if (windowSize >= distance) {
    return { start: 0, end: distance, leader };
  }

  const maximumStart = distance - windowSize;
  const followStart = leader - windowSize * 0.7;
  const start = Math.max(0, Math.min(maximumStart, followStart));
  return { start, end: start + windowSize, leader };
}

export function rankByDistance(positions: number[]): number[] {
  return positions
    .map((position, gate) => ({ gate, position }))
    .sort(
      (left, right) => right.position - left.position || left.gate - right.gate,
    )
    .map(({ gate }) => gate);
}

export function formatRaceTime(elapsedSeconds: number): string {
  const bounded = Math.max(0, elapsedSeconds);
  const minutes = Math.floor(bounded / 60);
  const seconds = bounded - minutes * 60;
  return `${minutes}:${seconds.toFixed(1).padStart(4, "0")}`;
}
